import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { Card, CardContent } from '../ui/Card'
import { Checkbox } from '../ui/Checkbox'
import { useAuthStore } from '../../stores/authStore'
import { FileText, ArrowRight, ArrowLeft, CheckCircle, Shield, Loader2 } from 'lucide-react'

export const TermsAcceptance: React.FC = () => {
  const navigate = useNavigate()
  const { 
    user,
    consentAccepted,
    isSubmitting,
    error,
    submitConsent,
    setCurrentStep 
  } = useAuthStore()

  const [consentData, setConsentData] = useState({
    termsAccepted: consentAccepted,
    privacyPolicyAccepted: consentAccepted,
    marketingOptIn: false,
    dataProcessingConsent: consentAccepted
  })

  const handleConsentChange = (field: keyof typeof consentData, checked: boolean) => {
    setConsentData(prev => ({ ...prev, [field]: checked }))
  }

  const handleContinue = async () => {
    const requiredConsents = ['termsAccepted', 'privacyPolicyAccepted', 'dataProcessingConsent'] as const
    const allRequiredAccepted = requiredConsents.every(field => consentData[field])
    
    if (!allRequiredAccepted) {
      return
    }

    try {
      await submitConsent(consentData)
      navigate('/onboarding')
    } catch (error) {
      // Error handled by store
    }
  }

  const handleBack = () => {
    setCurrentStep('otpVerification')
  }

  return (
    <Card className="shadow-lg border-0 bg-white max-w-lg mx-auto">
      <CardContent className="p-8">
        <div className="text-center mb-6">
          <div className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <FileText className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Terms & Consent
          </h2>
          <p className="text-gray-600">
            Please review and accept our terms to continue
          </p>
        </div>

        <div className="mb-6">
          <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto border">
            <h3 className="font-semibold text-gray-900 mb-3">Key Terms Overview</h3>
            
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <h4 className="font-medium text-gray-800 mb-1">Service Usage</h4>
                <p>By using StratIQX, you agree to use our strategic intelligence platform for legitimate business purposes only.</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-1">Data Privacy</h4>
                <p>We protect your business data with enterprise-grade security and will never share your information without consent.</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-1">Intelligence Reports</h4>
                <p>Our intelligence reports are for informational purposes and should be used alongside your business judgment.</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-1">Account Responsibilities</h4>
                <p>You are responsible for maintaining the security of your account and the accuracy of information provided.</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-1">Subscription & Billing</h4>
                <p>Subscription fees are billed according to your selected plan. You may cancel at any time.</p>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                This is a summary for your convenience. By proceeding, you agree to be bound by the complete{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a 
                  href="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Welcome to StratIQX!</h4>
                <p className="text-sm text-blue-800">
                  You're about to access personalized strategic intelligence for <strong>{user?.name}</strong>. 
                  Our platform will help you make data-driven business decisions with industry-specific insights.
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={consentData.termsAccepted && consentData.privacyPolicyAccepted && consentData.dataProcessingConsent}
              onCheckedChange={(checked) => {
                setConsentData({
                  termsAccepted: checked,
                  privacyPolicyAccepted: checked,
                  marketingOptIn: consentData.marketingOptIn,
                  dataProcessingConsent: checked
                })
              }}
              className="mt-1"
            />
            <div className="text-sm">
              <span className="text-gray-700">
                I have read and agree to the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a 
                  href="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Privacy Policy
                </a>
                . I understand that StratIQX will use my business information to provide personalized strategic intelligence insights.
              </span>
            </div>
          </label>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleContinue}
            disabled={!(consentData.termsAccepted && consentData.privacyPolicyAccepted && consentData.dataProcessingConsent) || isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-3 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Continue to StratIQX Platform
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Verification
          </Button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@stratiqx.ai" className="text-blue-600 hover:text-blue-800 underline">
              support@stratiqx.ai
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
